import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Mail, Lock, User } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { user, loginWithToken } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/app", { replace: true });
  }, [user, navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password };
      const { data } = await client.post(path, payload);
      await loginWithToken(data.token, data.user);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ho-bg">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-6">
        <button onClick={() => navigate("/")} data-testid="login-back"
          className="tap-scale inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-ho-muted">
          <ArrowLeft className="w-4 h-4" /> Home
        </button>

        <div className="flex flex-1 flex-col justify-center">
          <div className="mb-8 flex flex-col items-center text-center">
            <Logo />
            <h1 className="font-display mt-6 text-3xl font-black tracking-tighter">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h1>
            <p className="mt-2 text-ho-muted">
              {mode === "login" ? "Sign in to keep building." : "Start building on your phone — free."}
            </p>
          </div>

          <button onClick={googleLogin} data-testid="google-login"
            className="tap-scale flex w-full items-center justify-center gap-3 rounded-full border border-black/10 bg-white py-3.5 font-semibold">
            <GoogleIcon /> Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-black/10" />
            <span className="text-xs font-medium text-ho-muted">or with email</span>
            <div className="h-px flex-1 bg-black/10" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            <AnimatePresence>
              {mode === "register" && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                  <IconInput icon={User} type="text" placeholder="Full name" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} testid="register-name" />
                </motion.div>
              )}
            </AnimatePresence>
            <IconInput icon={Mail} type="email" placeholder="Email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} testid="login-email" />
            <IconInput icon={Lock} type="password" placeholder="Password" required minLength={6} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} testid="login-password" />
            <button type="submit" disabled={busy} data-testid="login-submit"
              className="tap-scale w-full rounded-full bg-ho-primary py-4 font-semibold text-white disabled:opacity-60">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-ho-muted">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="toggle-mode"
              className="font-semibold text-ho-primary">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

const IconInput = ({ icon: Icon, testid, ...props }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-4">
    <Icon className="w-5 h-5 text-ho-muted" />
    <input {...props} data-testid={testid}
      className="h-14 flex-1 bg-transparent text-[15px] outline-none" />
  </div>
);

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
  </svg>
);
